/**
 * カラーカタログ向けの自然順ソートユーティリティ.
 *
 * API レスポンスはメーカーの display_order → colors.name → colors.id でソートされるが、
 * 名前昇順のため code 順にならない（アステックで 9000 番台が 8000 番台より先に出るなど）。
 * メーカーグループの出現順（= API の display_order 順）を保ちつつ、同一メーカー内のみ
 * code の自然順（数値混在文字列対応）で stable ソートする。
 */

const codeCollator = new Intl.Collator('ja', { numeric: true, sensitivity: 'base' })

/**
 * メーカーのグループ出現順を維持しながら、同一メーカー内を code 昇順（自然順）でソートする.
 *
 * @param items - ソート対象の任意配列
 * @param getCode - 各要素から色コード（文字列）を取り出す関数
 * @param getManufacturer - 各要素からメーカー名（文字列）を取り出す関数
 * @returns メーカー初出順を保ったまま各グループ内を code 昇順でソートした新配列
 */
export function sortByColorCodeWithinManufacturer<T>(
  items: T[],
  getCode: (item: T) => string,
  getManufacturer: (item: T) => string,
): T[] {
  if (items.length === 0) return []

  // メーカーの初出順（= 入力配列における最初の登場位置）を記録する.
  const manufacturerOrder = new Map<string, number>()
  for (const item of items) {
    const mfr = getManufacturer(item)
    if (!manufacturerOrder.has(mfr)) {
      manufacturerOrder.set(mfr, manufacturerOrder.size)
    }
  }

  // メーカーごとにグルーピング（Map は挿入順を維持するため、初出順がそのまま出力順になる）.
  const groups = new Map<string, T[]>()
  for (const mfr of manufacturerOrder.keys()) {
    groups.set(mfr, [])
  }
  for (const item of items) {
    groups.get(getManufacturer(item))!.push(item)
  }

  // 各グループ内を code の自然順でソートして連結する.
  const result: T[] = []
  for (const group of groups.values()) {
    group.sort((a, b) => codeCollator.compare(getCode(a), getCode(b)))
    result.push(...group)
  }
  return result
}
